/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useIntl } from 'react-intl';
import { useQuery, useMutation, queryCache } from 'react-query';
import {
  getContracts,
  deleteContract,
  shareTenderContracts,
  reactivateContract,
} from '~src/services/api/contracts';
import {
  Button,
  Collapse,
  Spin,
  message,
  Empty,
  Tooltip,
  Checkbox,
  Dropdown,
  Menu,
  Typography,
} from 'antd';
import ContractBuilder from './ContractBuilder/ContractBuilder';
import { OfferBaseMetadata } from './ContractAndOffer';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './ContractBuilder/ContractBuilder.style';
import IconButton from '~components/_common/IconButton/IconButton';
import { ContractBuilderCreateContractModal } from '~components/popups-modals/ContractBuilderModals/ContractBuilderCreateContractModal/ContractBuilderCreateContractModal';
import AddOfferListModal from '~components/popups-modals/ContractBuilderModals/AddOfferListModal/AddOfferListModal';
import dataContextMenu, {
  DataContextMenuItemType,
} from '~src/data/dataContextMenu';
import { SimpleConfirmModal } from '~components/popups-modals/ConfirmModal/SimpleConfirmModal';
import { BaseRightClickOverlay } from '~components/atoms/RightClickOverlay/BaseRightClickOverlay';
import { ContractBuilderEditContractModal } from '~components/popups-modals/ContractBuilderModals/ContractBuilderEditContractModal/ContractBuilderEditContractModal';
import { actionOffer, createOffer } from '~src/services/api/shares';
import { DB_STATE } from '~src/types/enums';
import { getOfferStateDisplay } from '~src/utils/offerUtils/offerUtils';
import isTenderClosed from '~src/utils/isTenderClosed';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OfferNotificationModal } from '~components/popups-modals/OfferNotificationModal/OfferNotificationModal';

const { Panel } = Collapse;

type UseContractsUnifiedParams = {
  projectId?: number;
  tenderId?: string | number;
  shareId?: string;
  shareKey?: string;
  showDeleted?: boolean;
};

export const useContractsUnified = ({
  projectId,
  tenderId,
  shareId,
  shareKey,
  showDeleted,
}: UseContractsUnifiedParams) => {
  const isVendor = !!shareId && !!shareKey;

  return useQuery(
    ['contracts', projectId, tenderId, shareId, shareKey, showDeleted],
    () => {
      if (isVendor) {
        return shareTenderContracts({
          share_id: shareId!,
          Share_Key: shareKey!,
        });
      }
      return getContracts({
        projectId: projectId!,
        tenderId: tenderId!,
        show_deleted: showDeleted,
      });
    },
    {
      enabled: isVendor ? !!shareId && !!shareKey : !!projectId && !!tenderId,
      staleTime: 1000 * 60 * 5, // 5 minutes cache
      refetchOnWindowFocus: false,
    }
  );
};

const VoucherBuilder: React.FC<OfferBaseMetadata> = ({
  projectId,
  tenderId,
  shareId,
  shareKey,
  tenderRegData,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const isOnMyTender = !!shareId && !!shareKey;
  const tenderClosed = isTenderClosed(tenderRegData);

  // console.log(`tenderReg`, tenderRegData);

  const [showCreateContractModal, setShowCreateContractModal] = useState(false);
  const [showAddOfferListModal, setShowAddOfferListModal] = useState(false);
  const [showEditContractModal, setShowEditContractModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [contractToEdit, setContractToEdit] = useState<{
    id: number;
    name: string;
    description?: string;
    reference?: string;
  } | null>(null);
  const [rightClickedContractId, setRightClickedContractId] = useState<
    number | null
  >(null);
  const [contractToReject, setContractToReject] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [showConfirmRejectModal, setShowConfirmRejectModal] = useState(false);
  const [contractToFinalize, setContractToFinalize] = useState<{
    id: number;
    name: string;
    offerId?: number;
    state?: number;
  } | null>(null);
  const [showConfirmFinalizeModal, setShowConfirmFinalizeModal] = useState(
    false
  );
  const [contractToReopen, setContractToReopen] = useState<{
    id: number;
    name: string;
    offerId?: number;
    state?: number;
  } | null>(null);
  const [showConfirmReopenModal, setShowConfirmReopenModal] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [activeKeys, setActiveKeys] = useState<(string | number)[]>([]);
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [notificationModalVisible, setNotificationModalVisible] = useState(
    false
  );

  const didAutoOpenSingleContractRef = useRef(false);

  const { data: contracts, isLoading } = useContractsUnified({
    projectId,
    tenderId,
    shareId,
    shareKey,
    showDeleted,
  });

  useEffect(() => {
    if (!contracts || contracts.length !== 1) {
      didAutoOpenSingleContractRef.current = false;
      return;
    }

    if (didAutoOpenSingleContractRef.current) return;

    setActiveKeys([contracts[0].id]);
    didAutoOpenSingleContractRef.current = true;
  }, [contracts]);

  // Delete contract mutation
  const [doDeleteContract, { isLoading: isDeletingContract }] = useMutation(
    deleteContract,
    {
      onSuccess: () => {
        queryCache.invalidateQueries([
          'contracts',
          projectId,
          tenderId,
          shareId,
          shareKey,
        ]);
        message.success('Contract deleted successfully');
        setContractToDelete(null);
        setShowConfirmDeleteModal(false);
      },
      onError: (err) => {
        console.error('Delete Contract Error:', err);
        message.error('Failed to delete contract');
        setContractToDelete(null);
        setShowConfirmDeleteModal(false);
      },
    }
  );

  // Create Offer mutation
  const [doCreateOffer, { isLoading: isCreatingOffer }] = useMutation(
    createOffer,
    {
      onSuccess: () => {
        queryCache.invalidateQueries([
          'contracts',
          projectId,
          tenderId,
          shareId,
          shareKey,
        ]);
        message.success('Offer created successfully');
      },
      onError: (err) => {
        console.error('Delete Contract Error:', err);
        message.error('Failed to delete contract');
      },
    }
  );

  const [doRejectOffer, { isLoading: isRejecting }] = useMutation(actionOffer, {
    onSuccess: () => {
      queryCache.invalidateQueries([
        'contracts',
        projectId,
        tenderId,
        shareId,
        shareKey,
      ]);
      message.success('Offer rejected successfully');
      setShowConfirmRejectModal(false);
    },
    onError: (err) => {
      console.error('Failed to reject offer:', err);
      message.error('Failed to reject offer');
    },
  });

  const [doFinalizeOffer, { isLoading: isFinalizing }] = useMutation(
    actionOffer,
    {
      onSuccess: () => {
        message.success(intl.formatMessage({ id: 'offer-finalized-success' }));
        queryCache.invalidateQueries([
          'contracts',
          projectId,
          tenderId,
          shareId,
          shareKey,
        ]);
        setShowConfirmFinalizeModal(false);
      },
      onError: (err) => {
        console.error('Failed to finalize offer:', err);
        message.error(intl.formatMessage({ id: 'offer-finalize-failed' }));
      },
    }
  );

  const [doReopenOffer, { isLoading: isReopening }] = useMutation(actionOffer, {
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'offer-reopened-success' }));
      queryCache.invalidateQueries([
        'contracts',
        projectId,
        tenderId,
        shareId,
        shareKey,
      ]);
      setShowConfirmReopenModal(false);
    },
    onError: (err) => {
      console.error('Failed to reopen offer:', err);
      message.error(intl.formatMessage({ id: 'offer-reopen-failed' }));
    },
  });

  const [
    doReactivateContract,
    { isLoading: isReactivatingContract },
  ] = useMutation(reactivateContract, {
    onSuccess: () => {
      queryCache.invalidateQueries([
        'contracts',
        projectId,
        tenderId,
        shareId,
        shareKey,
      ]);
      message.success(
        intl.formatMessage({ id: 'contract-reactivated-success' })
      );
    },
    onError: (err) => {
      console.error('Reactivate Contract Error:', err);
      message.error(intl.formatMessage({ id: 'failed-reactivate-contract' }));
    },
  });

  // Handle context menu click
  const handleContextMenuClick = (item: DataContextMenuItemType) => {
    if (!rightClickedContractId) return;
    if (!isOnMyTender && tenderClosed) return;

    if (item.modal === 'reactivate-contract') {
      const contractToReactivate = contracts?.find(
        (c) => c.id === rightClickedContractId
      );
      if (!contractToReactivate) return;

      doReactivateContract({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: contractToReactivate.id,
      });
    }

    if (item.modal === 'delete') {
      const contractToDelete = contracts?.find(
        (c) => c.id === rightClickedContractId
      );
      if (!contractToDelete) return;

      setContractToDelete({
        id: contractToDelete.id,
        name: contractToDelete.name,
      });
      setShowConfirmDeleteModal(true);
    }

    if (item.modal === 'edit-contract') {
      const contractToEdit = contracts?.find(
        (c) => c.id === rightClickedContractId
      );

      if (!contractToEdit) return;

      setContractToEdit({
        id: contractToEdit.id,
        name: contractToEdit.name,
        description: contractToEdit?.description,
        reference: contractToEdit?.reference,
      });

      setShowEditContractModal(true);
    }

    if (item.modal === 'reject-offer') {
      const contractToReject = contracts?.find(
        (c) => c.id === rightClickedContractId
      );
      if (!contractToReject) return;

      setContractToReject({
        id: contractToReject.id,
        name: contractToReject.name,
      });
      setShowConfirmRejectModal(true);
    }

    if (item.modal === 'finalize-offer') {
      const contractToFinalize = contracts?.find(
        (c) => c.id === rightClickedContractId
      );
      if (!contractToFinalize) return;

      setContractToFinalize({
        id: contractToFinalize.id,
        name: contractToFinalize.name,
        offerId: contractToFinalize.offer_options?.offer_id,
        state: contractToFinalize.offer_options?.state,
      });
      setShowConfirmFinalizeModal(true);
    }

    if (item.modal === 'reopen-offer') {
      const contractToReopen = contracts?.find(
        (c) => c.id === rightClickedContractId
      );
      if (!contractToReopen) return;

      setContractToReopen({
        id: contractToReopen.id,
        name: contractToReopen.name,
        offerId: contractToReopen.offer_options?.offer_id,
        state: contractToReopen.offer_options?.state,
      });
      setShowConfirmReopenModal(true);
    }
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!contractToDelete) return;

    try {
      await doDeleteContract({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: contractToDelete.id,
      });
    } catch (error) {
      console.error('Error in handleConfirmDelete:', error);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setContractToDelete(null);
    setShowConfirmDeleteModal(false);
  };

  const createActionsMenu = (
    <Menu
      css={{
        minWidth: 260,
        borderRadius: 8,
        padding: '4px 0',
      }}>
      <Menu.ItemGroup
        title={intl.formatMessage({ id: 'contract-shortcut-section' })}>
        <Menu.Item
          key="add-offer-list"
          disabled={tenderClosed}
          onClick={() => setShowAddOfferListModal(true)}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
            <IconButton
              color={tenderClosed ? 'grey' : 'blue'}
              backgroundOff={true}
              icon={['far', 'briefcase']}
              customStyle={{ fontSize: '18px' }}
            />
            <span>{intl.formatMessage({ id: 'add-offer-list' })}</span>
          </div>
        </Menu.Item>
      </Menu.ItemGroup>

      <Menu.Divider />

      <Menu.ItemGroup
        title={intl.formatMessage({ id: 'contract-builder-section' })}>
        <Menu.Item
          key="add-contract"
          disabled={tenderClosed}
          onClick={() => setShowCreateContractModal(true)}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
            <IconButton
              color={tenderClosed ? 'grey' : 'blue'}
              backgroundOff={true}
              icon={['far', 'square-dashed-circle-plus']}
              customStyle={{ fontSize: '18px' }}
            />
            <span>{intl.formatMessage({ id: 'add-contract' })}</span>
          </div>
        </Menu.Item>
      </Menu.ItemGroup>
    </Menu>
  );

  if (isLoading) {
    return <Spin css={{ marginTop: '30px' }} />;
  }

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: 0,
        // border: '2px solid green',
      }}>
      <div
        css={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          paddingBottom: '20px',
          // backgroundColor: 'rgba(0, 255, 0, 0.1)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          marginBottom: '5px',
        }}>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
          }}>
          {!isOnMyTender && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                gap: 12,
              }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}>
                <Tooltip
                  title={intl.formatMessage({
                    id: 'notification-letters-settings',
                  })}>
                  <Button
                    style={{
                      borderColor: '#ff9800',
                      color: '#ff9800',
                      background: 'transparent',
                      width: 40,
                      height: 40,
                      minWidth: 40,
                      padding: 0,
                      borderRadius: 6,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onClick={() => setNotificationModalVisible(true)}>
                    <FontAwesomeIcon
                      icon={['far', 'bell']}
                      style={{
                        fontSize: '16px',
                        color: '#ff9800',
                      }}
                    />
                  </Button>
                </Tooltip>

                <Dropdown
                  overlay={createActionsMenu}
                  trigger={['click']}
                  placement="bottomLeft"
                  visible={isCreateMenuOpen}
                  onVisibleChange={(visible) => setIsCreateMenuOpen(visible)}>
                  <Button
                    type="primary"
                    disabled={tenderClosed}
                    onClick={(e) => e.preventDefault()}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 40,
                      padding: '0 16px',
                      borderRadius: 6,
                      fontWeight: 500,
                      boxShadow: 'none',
                    }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 12,
                      }}>
                      <FontAwesomeIcon
                        icon={['far', 'plus']}
                        style={{
                          fontSize: '13px',
                          color: 'white',
                          opacity: tenderClosed ? 0.7 : 1,
                        }}
                      />
                      <span>{intl.formatMessage({ id: 'create-new' })}</span>

                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 10,
                        }}>
                        <FontAwesomeIcon
                          icon={
                            isCreateMenuOpen
                              ? ['fas', 'chevron-up']
                              : ['fas', 'chevron-down']
                          }
                          style={{
                            fontSize: '11px',
                            color: 'white',
                            opacity: tenderClosed ? 0.7 : 0.95,
                            transition: '0.15s ease',
                          }}
                        />
                      </span>
                    </span>
                  </Button>
                </Dropdown>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                }}>
                <Checkbox
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}>
                  {intl.formatMessage({ id: 'show-deleted' })}
                </Checkbox>
              </div>
            </div>
          )}
          {isOnMyTender && contracts && (
            <div css={{ display: 'flex', gap: '10px' }}>
              <Button
                onClick={() => {
                  setActiveKeys(contracts.map((c: any) => c.id));
                }}
                type="primary">
                {intl.formatMessage({ id: 'show-all' })}
              </Button>
              <Button
                onClick={() => {
                  setActiveKeys([]);
                }}>
                {intl.formatMessage({ id: 'close-all' })}
              </Button>
            </div>
          )}
        </div>
      </div>

      {contracts && contracts.length > 0 ? (
        /* SCROLL AREA: keeps header above unscrollable */
        <div
          css={{
            flex: 1,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            // Height cap so the list doesn't push the page
            // maxHeight: 'calc(100vh - 300px)',
            minHeight: 0,
            paddingRight: '2px',
            // paddingBottom: '80px',
          }}>
          <Collapse
            bordered
            accordion={!isOnMyTender}
            activeKey={activeKeys}
            onChange={(keys) =>
              setActiveKeys(Array.isArray(keys) ? keys : [keys])
            }
            destroyInactivePanel={true}
            style={{ backgroundColor: 'white' }}>
            {contracts.map((contract: any) => {
              // Compare keys by converting to string to ensure matching
              const isOpen = activeKeys.some(
                (k) => String(k) === String(contract.id)
              );

              return (
                <Panel
                  header={
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        gap: '10px',
                      }}>
                      {/* Status Icon | Project Side Only | Toggle based on isOpen */}
                      <div
                        style={{
                          flex: '0 0 20px',
                          display: 'flex',
                          justifyContent: 'center',
                        }}>
                        <Tooltip
                          title={intl.formatMessage({
                            id: isOpen ? 'close' : 'open',
                          })}>
                          <FontAwesomeIcon
                            icon={
                              isOpen
                                ? ['fas', 'chevron-up']
                                : ['fas', 'chevron-down']
                            }
                            style={{
                              fontSize: '12px',
                              color:
                                contract.state === 9 ? '#f82609' : '#595959',
                            }}
                          />
                        </Tooltip>
                      </div>
                      {/* 1. Reference Column */}
                      <div style={{ flex: '0 0 140px', overflow: 'hidden' }}>
                        <span css={{ color: 'gray', fontSize: '10px' }}>
                          #
                          {contract.reference
                            ? contract.reference
                            : intl.formatMessage({ id: 'no-reference' })}
                        </span>
                      </div>

                      {/* 2. Title Column */}
                      <div style={{ flex: '0 0 220px' }}>{contract.name}</div>

                      {/* 3. Description Column  */}
                      <div
                        style={{
                          flex: '1 1 auto',
                          color: 'grey',
                          fontWeight: 'lighter',
                          fontSize: '13px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                        {contract.description ||
                          intl.formatMessage({
                            id: 'contract-description-not-provided',
                          })}
                      </div>

                      {/* 4. Vendor Side (Validation + Btn) */}
                      {isOnMyTender && (
                        <div
                          css={{
                            flex: '0 0 auto',
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}>
                          {/* VALIDATION COLUMN */}
                          <div
                            style={{
                              width: '50px',
                              display: 'flex',
                              justifyContent: 'flex-end',
                            }}>
                            {contract?.offer_options?.is_offer_created && (
                              <div
                                css={{
                                  color:
                                    contract.offer_options.state ===
                                    DB_STATE.ACCEPTED
                                      ? '#52c41a'
                                      : contract.offer_options.state ===
                                        DB_STATE.REJECTED
                                      ? '#ff4d4f'
                                      : 'gray',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                }}>
                                {/* Real statistics from backend */}
                                {contract.offer_options.statistic
                                  ? `${contract.offer_options.statistic.voucher_responded}/${contract.offer_options.statistic.voucher_total}`
                                  : '-/-'}
                              </div>
                            )}
                          </div>

                          {/* BUTTON COLUMN */}
                          <div
                            style={{
                              minWidth: '260px',
                              display: 'flex',
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                              gap: '8px',
                            }}>
                            {contract?.offer_options?.is_offer_created ? (
                              getOfferStateDisplay(
                                contract.offer_options.state,
                                intl
                              )
                            ) : (
                              <Button
                                type={'primary'}
                                disabled={
                                  tenderClosed ||
                                  contract?.offer_options?.is_offer_created
                                }
                                style={{
                                  minWidth: '112px',
                                  fontSize: '14px',
                                  padding: '4px 15px',
                                  height: 'auto',
                                  margin: 0,
                                }}
                                loading={isCreatingOffer}
                                onClick={async () => {
                                  try {
                                    await doCreateOffer({
                                      share_id: shareId!,
                                      share_key: shareKey!,
                                      contract_id: contract.id,
                                    });
                                  } catch (error) {
                                    console.error(error);
                                  }
                                }}>
                                {intl.formatMessage({
                                  id: 'offer-state-start',
                                })}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Empty/unfinished contract warning - project side
                          only, since the vendor-side response has no
                          has_tender_voucher field. */}
                      {!isOnMyTender && contract.has_tender_voucher === false && (
                        <div
                          style={{
                            flex: '0 0 24px',
                            display: 'flex',
                            justifyContent: 'center',
                          }}>
                          <Tooltip
                            title={intl.formatMessage({
                              id: 'contract-no-voucher-warning',
                            })}>
                            <FontAwesomeIcon
                              icon={['far', 'triangle-exclamation']}
                              style={{ fontSize: '16px', color: '#722ed1' }}
                            />
                          </Tooltip>
                        </div>
                      )}

                      {/* 5. Properties (Context Menu) - Far right */}
                      <div
                        style={{ flex: '0 0 auto' }}
                        onClick={(e) => e.stopPropagation()}>
                        <BaseRightClickOverlay
                          items={
                            isOnMyTender
                              ? (() => {
                                  const offerState =
                                    contract.offer_options?.state;
                                  const canFinalize =
                                    offerState === 1 || offerState === 3; // Draft or Reopened
                                  const canReopen =
                                    offerState === 4 || offerState === 5; // Accepted or Rejected

                                  return dataContextMenu.vendorContractContext
                                    .filter((item) => {
                                      if (item.modal === 'finalize-offer')
                                        return canFinalize;
                                      if (item.modal === 'reopen-offer')
                                        return canReopen;
                                      return true; // Keep reject for all states
                                    })
                                    .map((item) => ({
                                      ...item,
                                      disabled: tenderClosed,
                                    }));
                                })()
                              : (contract.state === 9
                                  ? dataContextMenu.deletedContractContext
                                  : dataContextMenu.contractContext
                                ).map((item) => ({
                                  ...item,
                                  disabled: tenderClosed,
                                }))
                          }
                          onItemClick={handleContextMenuClick}
                          onOverlayClose={() =>
                            setRightClickedContractId(null)
                          }>
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}>
                            <IconButton
                              icon={['far', 'ellipsis-v']}
                              widthTooltip={intl.formatMessage({
                                id: 'settings',
                              })}
                              customStyle={{
                                cursor:
                                  !isOnMyTender && tenderClosed
                                    ? 'not-allowed'
                                    : 'pointer',
                                opacity:
                                  !isOnMyTender && tenderClosed ? 0.5 : 1,
                              }}
                              click={(e) => {
                                e.preventDefault();
                                setRightClickedContractId(contract.id);
                                const customEvent = new MouseEvent(
                                  'contextmenu',
                                  {
                                    bubbles: true,
                                    cancelable: true,
                                    view: window,
                                    button: 2,
                                    buttons: 2,
                                    clientX: e.clientX,
                                    clientY: e.clientY,
                                  }
                                );
                                e.currentTarget.dispatchEvent(customEvent);
                              }}
                            />
                          </div>
                        </BaseRightClickOverlay>
                      </div>
                    </div>
                  }
                  showArrow={false}
                  key={contract.id}
                  style={{
                    fontWeight: 'bold',
                    fontSize: '16px',
                    backgroundColor:
                      contract.state === 9
                        ? '#fff2f0'
                        : !isOnMyTender && contract.has_tender_voucher === false
                        ? '#fff3e0'
                        : '#fafafa',
                    border:
                      contract.state === 9 ? '1px solid #ffccc7' : undefined,
                    opacity: contract.state === 9 ? 0.88 : 1,
                  }}
                  css={styles.panelD}>
                  <ContractBuilder
                    projectId={projectId}
                    tenderId={tenderId}
                    contractId={contract.id}
                    shareId={shareId}
                    shareKey={shareKey}
                    isOnMyTender={isOnMyTender}
                    tenderClosed={tenderClosed}
                    contractOfferData={contract.offer_options}
                    showDeleted={showDeleted}
                    contractDeleted={contract.state === 9}
                  />
                </Panel>
              );
            })}
          </Collapse>
        </div>
      ) : (
        <Empty
          css={{
            flex: 1,
            minHeight: 0,
            padding: '5vh 0',
            margin: '0',
            border: '1px solid #E3E4E4',
            borderRadius: '5px',
            whiteSpace: 'pre-wrap',
          }}
          description={intl.formatMessage({
            id: 'projects-tender-contracts-empty-list-head',
          })}>
          {intl.formatMessage({
            id: 'projects-tender-contracts-empty-list-content',
          })}
        </Empty>
      )}

      {/* Create Contract Modal */}
      <ContractBuilderCreateContractModal
        projectId={projectId}
        tenderId={tenderId}
        visible={showCreateContractModal}
        onCancel={() => setShowCreateContractModal(false)}
      />

      {/* Add Offer List Shortcut Modal */}
      <AddOfferListModal
        projectId={projectId}
        tenderId={tenderId}
        registrationReferenceNr={tenderRegData?.reference}
        visible={showAddOfferListModal}
        onCancel={() => setShowAddOfferListModal(false)}
      />

      {/* Edit Contract Modal */}
      <ContractBuilderEditContractModal
        projectId={Number(projectId)}
        tenderId={Number(tenderId)}
        visible={showEditContractModal}
        onCancel={() => setShowEditContractModal(false)}
        data={contractToEdit}
      />

      {/* Delete Contract Confirmation Modal */}
      {showConfirmDeleteModal && contractToDelete && (
        <SimpleConfirmModal
          visible={showConfirmDeleteModal}
          isLoading={isDeletingContract}
          okButtonProps={{
            disabled: tenderClosed,
          }}
          onOk={handleConfirmDelete}
          onCancel={handleCancelDelete}
          modalTitle={`${intl.formatMessage({ id: 'delete-contract' })} - ${
            contractToDelete.name
          }`}
          modalIcon={['far', 'trash']}
          okText={intl.formatMessage({ id: 'delete' })}
          okType="danger"
        />
      )}

      {/* Reject Offer | Vendor side */}
      {showConfirmRejectModal && contractToReject && (
        <SimpleConfirmModal
          visible={showConfirmRejectModal}
          isLoading={isRejecting}
          okButtonProps={{
            loading: isRejecting,
            disabled:
              tenderClosed ||
              (!contracts.find((c: any) => c.id === contractToReject.id)
                ?.offer_options?.is_offer_created ??
                true),
          }}
          onOk={() => {
            const selectedOfferId = contracts.find(
              (c: any) => c.id === contractToReject.id
            )?.offer_options?.offer_id;

            doRejectOffer({
              share_id: shareId!,
              share_key: shareKey!,
              offer_id: selectedOfferId,
              action: 'reject',
            });
          }}
          onCancel={() => setShowConfirmRejectModal(false)}
          modalTitle={`Reject "${contractToReject.name}" offer?`}
          modalIcon={['far', 'ban']}
          okText="Reject"
          okType="danger"
          destroyOnClose
        />
      )}
      {/* Finalize Offer Confirmation Modal */}
      {showConfirmFinalizeModal && contractToFinalize && (
        <SimpleConfirmModal
          visible={showConfirmFinalizeModal}
          isLoading={isFinalizing}
          modalTitle={intl.formatMessage({
            id: 'confirm-finalize-offer-title',
          })}
          modalIcon={['far', 'check-circle']}
          onCancel={() => setShowConfirmFinalizeModal(false)}
          onOk={() => {
            if (!contractToFinalize.offerId) return;
            if (
              contractToFinalize.state !== 1 &&
              contractToFinalize.state !== 3
            ) {
              message.error(
                intl.formatMessage({ id: 'offer-cannot-finalize' })
              );
              setShowConfirmFinalizeModal(false);
              return;
            }
            doFinalizeOffer({
              share_id: shareId!,
              share_key: shareKey!,
              offer_id: contractToFinalize.offerId,
              action: 'accept',
            });
          }}
          okText={intl.formatMessage({ id: 'finalize-offer' })}
          okType="primary"
          destroyOnClose>
          <Typography.Paragraph style={{ padding: '20px' }}>
            {intl.formatMessage({ id: 'confirm-finalize-offer-message' })}
          </Typography.Paragraph>
        </SimpleConfirmModal>
      )}
      {/* Reopen Offer Confirmation Modal */}
      {showConfirmReopenModal && contractToReopen && (
        <SimpleConfirmModal
          visible={showConfirmReopenModal}
          isLoading={isReopening}
          modalTitle={intl.formatMessage({ id: 'confirm-reopen-offer-title' })}
          modalIcon={['far', 'redo']}
          okButtonProps={{
            style: {
              backgroundColor: '#ff9800',
              borderColor: '#ff9800',
              color: 'white',
            },
          }}
          onCancel={() => setShowConfirmReopenModal(false)}
          onOk={() => {
            if (!contractToReopen.offerId) return;
            if (contractToReopen.state !== 4 && contractToReopen.state !== 5) {
              message.error(intl.formatMessage({ id: 'offer-cannot-reopen' }));
              setShowConfirmReopenModal(false);
              return;
            }
            doReopenOffer({
              share_id: shareId!,
              share_key: shareKey!,
              offer_id: contractToReopen.offerId,
              action: 'reopen',
            });
          }}
          okText={intl.formatMessage({ id: 'offer-reopen' })}
          okType="default"
          destroyOnClose>
          <Typography.Paragraph style={{ padding: '20px' }}>
            {intl.formatMessage({ id: 'confirm-reopen-offer-message' })}
          </Typography.Paragraph>
        </SimpleConfirmModal>
      )}

      {/* Offer Notification Settings Modal */}
      {!isOnMyTender && (
        <OfferNotificationModal
          visible={notificationModalVisible}
          onClose={() => setNotificationModalVisible(false)}
          projectId={Number(projectId)}
          tenderId={Number(tenderId)}
          tenderClosed={tenderClosed}
        />
      )}
    </div>
  );
};

export default VoucherBuilder;
